import { Component, Inject, OnInit } from '@angular/core';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import { IPaymentTemplateBuildTypeDto, LOT_CONTRACT_TYPE_ENUM } from '@classictechsolutions/hubapi-transpiled-enums';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { PaymentTemplateStageDialogComponent } from '../payment-template-stage-dialog/payment-template-stage-dialog.component';
import { PaymentTemplateBuildTypeDialogComponent } from '../payment-template-build-type-dialog/payment-template-build-type-dialog.component';
import { IPaymentTemplateStageDto } from '@app/logic/payment-templates/interface/i.payment-template.dto';
import { ToastService } from '@app/core/services/toast/toast.service';
import { Observable, take } from 'rxjs';
import { IPaymentTemplateMappedItem } from '@app/logic/payment-templates/interface/i.payment-template.mapped';
import { FormMode } from '@app/shared/enums/form';
import { orderBy } from 'lodash';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';


@Component({
    templateUrl: './payment-template-dialog.component.html',
    styleUrls: ['./payment-template-dialog.component.scss'],
    providers: [],
    standalone: false
})
export class PaymentTemplateDialogComponent extends BaseDialogFormViewDirective<any, any, any> implements OnInit {
    public static readonly MIN_WIDTH = '40%';

    public readonly LOT_CONTRACT_TYPE_ENUM = LOT_CONTRACT_TYPE_ENUM;

    public dataChanged = false;

    public readonly stageColumns = [
        'Trigger',
        'Payment Amount',
        'Amount Type',
        '',
    ];

    public readonly buildTypeColumns = [
        { label: 'Contract Type' },
        { label: 'Code' },
        { label: 'Build Type' },
        { label: 'Active', width: 100 },
        { label: 'Default', width: 100 },
        { label: '' }
    ];

    public get mappedItem(): IPaymentTemplateMappedItem {
        return this.data.mappedItem;
    }

    constructor(
        public readonly dialogRef: MatDialogRef<PaymentTemplateDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: {
            mappedItem: IPaymentTemplateMappedItem;
            existingBuildTypes: IPaymentTemplateBuildTypeDto[];
        },
        public readonly toastService: ToastService,
        private readonly cbDialog: CbDialogService,
    ) {
        super(dialogRef, toastService);
    }

    public ngOnInit(): void {
        if (this.mappedItem.id > 0) {
            this.formMode = FormMode.Edit;
        } else {
            this.formMode = FormMode.Add;
        }
        // Back end seems to send this reversed so just sort once on startup
        this.mappedItem.stages = orderBy(this.mappedItem.stages, 'sortOrder');
    }

    public onDrop(event: CdkDragDrop<IPaymentTemplateStageDto[]>) {
        if (event.previousIndex === event.currentIndex) return;

        moveItemInArray(this.mappedItem.stages, event.previousIndex, event.currentIndex);

        this.rebuildStageSortOrder();
        this.dataChanged = true;
    }

    public addPaymentStage(): void {
        this.managePaymentStage(this.newPaymentTemplateStage())
            .pipe(take(1))
            .subscribe((result) => {
                if (result) {
                    this.mappedItem.stages.push(result);
                    this.rebuildStageSortOrder();

                    this.dataChanged = true;
                }
            });
    }

    public editPaymentStage(paymentTemplateStage: IPaymentTemplateStageDto): void {
        this.managePaymentStage(paymentTemplateStage)
            .pipe(take(1)).subscribe((result) => {
                if (result) {
                    Object.assign(paymentTemplateStage, result);
                    this.dataChanged = true;
                }
            });
    }

    private managePaymentStage(paymentTemplateStage: IPaymentTemplateStageDto): Observable<any> {
        const data = {
            paymentTemplateStage,
            existingStages: this.mappedItem.stages
        };
        return this.cbDialog
            .open(PaymentTemplateStageDialogComponent, {
                data,
                minWidth: '45%',
            })
            .afterClosed();
    }

    public removePaymentStage(index: number): void {
        this.mappedItem.stages.splice(index, 1);

        this.rebuildStageSortOrder();

        this.dataChanged = true;
    }

    public addBuildType(): void {
        this.mappedItem.buildTypes = this.mappedItem.buildTypes || [];
        const data = {
            existingBuildTypes: this.mappedItem.buildTypes,
            paymentTemplateBuildType: this.newPaymentTemplateBuildType(),
        };
        this.cbDialog.open(PaymentTemplateBuildTypeDialogComponent, {
            data,
            minWidth: '45%',
        })
            .afterClosed()
            .pipe(take(1)).subscribe((result) => {
                if (result) {
                    this.mappedItem.buildTypes.push(result);
                    this.dataChanged = true;
                }
            });
    }

    public editBuildType(buildType: IPaymentTemplateBuildTypeDto, $index: number): void {
        this.mappedItem.buildTypes = this.mappedItem.buildTypes || [];
        const data = {
            existingBuildTypes: this.mappedItem.buildTypes,
            paymentTemplateBuildType: this.newPaymentTemplateBuildType(),
            selectedContractTypeId: buildType.lotContractType,
            selectedBuildTypeId: buildType.buildTypeId,
            isDefaultPaymentBuildType: buildType.isDefault,
            paymentTemplateId: this.mappedItem.id
        };

        this.cbDialog.open(PaymentTemplateBuildTypeDialogComponent, {
            data,
            minWidth: '45%',
        })
            .afterClosed()
            .pipe(take(1)).subscribe((result) => {
                if (result) {
                    this.mappedItem.buildTypes[$index] = result;
                    this.dataChanged = true;
                }
            });
    }

    public removeBuildType(index): void {
        if (this.mappedItem.buildTypes[index].isDefault) {
            this.cbDialog.confirm({
                message: 'This template is set as the default for this Build Type. If you remove it you must set the new default in the build type settings',
                yesLabel: 'Delete',
                noLabel: 'Cancel',
                confirmed: () => {
                    this.mappedItem.buildTypes.splice(index, 1);
                    this.dataChanged = true;
                }
            });
        } else {
            this.mappedItem.buildTypes.splice(index, 1);
            this.dataChanged = true;
        }
    }

    private newPaymentTemplateStage(): IPaymentTemplateStageDto {
        return {
            amountType: 1,
            buildStage: {
            },
        } as IPaymentTemplateStageDto;
    }

    private newPaymentTemplateBuildType(): IPaymentTemplateBuildTypeDto {
        return {} as IPaymentTemplateBuildTypeDto;
    }

    private rebuildStageSortOrder(): void {
        if (!this.mappedItem.stages) return;

        this.mappedItem.stages.forEach((stage, index) => {
            stage.sortOrder = index + 1;
        });
    }
}
